import { call, put, select, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { getSearchedPortsByNameApi, getPortGeometryByNameAndCountryApi } from './portApi';
import { portSagaActionTypes, IPortSearchForm, IPortGeometry } from './portTypes';
import { trackEventWithContext, trackExceptionWithContext } from '../../services/application-insights/appInsights';
import {
	AppInsightsActions,
	AppInsightsComponents,
	AppInsightsErrorTypes,
} from '../../services/application-insights/appInsightsConstants';
import {
	searchPortsFailure,
	searchPortsSuccess,
	getPortGeometryByNameAndCountrySuccess,
	getPortGeometryByNameAndCountryFailure,
} from './portSlice';
import { selectVesselsLastSignalData } from '../main-map/mapSelectors';
import { selectSubscribedMsiVessels } from '../../user-management/appSelectors';
import { setSelectedVesselsForOutputRequest } from '../search/searchSlice';
import { TSubscribedMsiVessel } from '../../user-management/subscription/subscriptionTypes';
import { EOutputType, ESelectionType } from '../../shared/sharedEnums';
import { ISelectedVesselsFormData, IVesselFormData, IPortFormData } from '../../shared/sharedTypes';
import { TFeature, TVesselInfo } from '../main-map/mapTypes';
import * as turf from '@turf/turf';

function* searchPortsSaga(action: PayloadAction<{ portName: string }>) {
	try {
		// Track search attempt
		trackEventWithContext(AppInsightsActions.SEARCH_QUERY_SUBMIT, {
			component: AppInsightsComponents.SEARCH_SLICE,
			action: AppInsightsActions.SEARCH_QUERY_SUBMIT,
			additionalData: {
				searchType: 'ports',
				searchTerm: action.payload.portName,
			},
		});

		const response: { ports: IPortSearchForm[] } = yield call(getSearchedPortsByNameApi, action.payload.portName);

		yield put(searchPortsSuccess({ ports: response.ports }));

		// Track successful search
		trackEventWithContext(AppInsightsActions.SEARCH_QUERY_SUBMIT, {
			component: AppInsightsComponents.SEARCH_SLICE,
			action: AppInsightsActions.SEARCH_QUERY_SUBMIT,
			additionalData: {
				searchType: 'ports',
				searchTerm: action.payload.portName,
				status: 'success',
			},
		});
	} catch (error) {
		const errorMessage = error instanceof Error ? error.message : 'Unknown search error';

		yield put(searchPortsFailure({ error: errorMessage }));

		// Track search error
		trackExceptionWithContext(error as Error, {
			component: AppInsightsComponents.SEARCH_SLICE,
			action: AppInsightsActions.SEARCH_QUERY_SUBMIT,
			additionalData: {
				errorType: AppInsightsErrorTypes.SEARCH_ENTITY_NOT_FOUND_ERROR,
				searchType: 'ports',
				searchTerm: action.payload.portName,
			},
		});
	}
}

function* getPortGeometryByNameAndCountrySaga(action: PayloadAction<{ portName: string; country: string }>) {
	try {
		// Track port geometry fetch attempt
		trackEventWithContext(AppInsightsActions.SEARCH_QUERY_SUBMIT, {
			component: AppInsightsComponents.SEARCH_SLICE,
			action: AppInsightsActions.SEARCH_QUERY_SUBMIT,
			additionalData: {
				searchType: 'get-port-geometry',
				portName: action.payload.portName,
				country: action.payload.country,
			},
		});

		const response: IPortGeometry = yield call(
			getPortGeometryByNameAndCountryApi,
			action.payload.portName,
			action.payload.country,
		);

		yield put(
			getPortGeometryByNameAndCountrySuccess({
				portGeometry: response,
				portName: action.payload.portName,
				country: action.payload.country,
			}),
		);

		// Track successful port geometry fetch
		trackEventWithContext(AppInsightsActions.SEARCH_QUERY_SUBMIT, {
			component: AppInsightsComponents.SEARCH_SLICE,
			action: AppInsightsActions.SEARCH_QUERY_SUBMIT,
			additionalData: {
				searchType: 'get-port-geometry',
				portName: action.payload.portName,
				country: action.payload.country,
				status: 'success',
			},
		});
	} catch (error) {
		yield put(getPortGeometryByNameAndCountryFailure());
		// Track port geometry error
		trackExceptionWithContext(error as Error, {
			component: AppInsightsComponents.SEARCH_SLICE,
			action: AppInsightsActions.SEARCH_QUERY_SUBMIT,
			additionalData: {
				errorType: AppInsightsErrorTypes.SEARCH_ENTITY_NOT_FOUND_ERROR,
				searchType: 'get-port-geometry',
				portName: action.payload.portName,
				country: action.payload.country,
			},
		});
	}
}

function* displayOutputByPortGeometrySaga(action: PayloadAction<{ portGeometry: IPortGeometry }>) {
	try {
		if (!action.payload.portGeometry?.geometry) {
			throw new Error('Invalid port geometry data');
		}

		const { properties } = action.payload.portGeometry;
		if (!properties?.portName) {
			throw new Error('Port name is missing from geometry data');
		}

		// Track port geometry fetch attempt
		trackEventWithContext(AppInsightsActions.SEARCH_QUERY_SUBMIT, {
			component: AppInsightsComponents.SEARCH_SLICE,
			action: AppInsightsActions.SEARCH_QUERY_SUBMIT,
			additionalData: {
				searchType: 'port-geometry',
				portName: properties.portName,
				country: properties.country || 'Unknown',
			},
		});
		// Get vessels last signal data
		const vesselsLastSignalData: TVesselInfo = yield select(selectVesselsLastSignalData);

		if (!vesselsLastSignalData?.features?.length) {
			console.warn('No vessel signal data available for port analysis');
		}

		// Filter vessels: lastSpeed < 1 and within port geometry
		let filteredVesselsInPort: number[] = [];
		if (vesselsLastSignalData?.features && action.payload.portGeometry.geometry) {
			const portGeometry = action.payload.portGeometry.geometry as GeoJSON.Polygon | GeoJSON.MultiPolygon;

			filteredVesselsInPort = vesselsLastSignalData.features
				.filter((vessel: TFeature) => {
					if (!vessel.properties) return false;

					if (
						typeof vessel.properties.lastSpeed !== 'number' ||
						typeof vessel.properties.longitude !== 'number' ||
						typeof vessel.properties.latitude !== 'number'
					) {
						return false;
					}

					// Check if lastSpeed < 1
					const hasLowSpeed = vessel.properties.lastSpeed < 1;

					// Check if vessel is within port geometry using turf
					try {
						const vesselPoint = turf.point([vessel.properties.longitude, vessel.properties.latitude]);
						const isWithinPort = turf.booleanPointInPolygon(vesselPoint, portGeometry);
						return hasLowSpeed && isWithinPort;
					} catch (geoError) {
						console.warn('Failed to check vessel position:', geoError);
						return false;
					}
				})
				.map((vessel: TFeature) => vessel.properties!.imoNumber);
		}

		// Get subscribed vessels and filter by IMO numbers from above
		const subscribedVessels: TSubscribedMsiVessel[] = yield select(selectSubscribedMsiVessels);
		const filteredSubscribedVessels = subscribedVessels
			.filter((vessel) => filteredVesselsInPort.includes(vessel.imoNumber))
			.map((vessel) => ({
				name: vessel.vesselName,
				imoNumber: vessel.imoNumber,
				sectorName: vessel.sectorName,
				companyName: vessel.owner,
			})) as IVesselFormData[];

		// Create output selection for the filtered vessels
		const outputSelection: ISelectedVesselsFormData = {
			selectedVesselsList: filteredSubscribedVessels,
			selectionType: ESelectionType.Port,
			selectionName: action.payload.portGeometry.properties.portName,
			outputType:
				filteredSubscribedVessels.length <= 0
					? EOutputType.None
					: filteredSubscribedVessels.length === 1
						? EOutputType.Vessel
						: filteredSubscribedVessels.length <= 200
							? EOutputType.Fleet
							: EOutputType.Summary,
			selectionTypeObject: {
				name: action.payload.portGeometry.properties.portName,
				country: action.payload.portGeometry.properties.country,
			} as IPortFormData,
		};

		// Dispatch the filtered vessels for output
		yield put(setSelectedVesselsForOutputRequest({ outputSelection }));


		// Track successful port geometry fetch
		trackEventWithContext(AppInsightsActions.SEARCH_QUERY_SUBMIT, {
			component: AppInsightsComponents.SEARCH_SLICE,
			action: AppInsightsActions.SEARCH_QUERY_SUBMIT,
			additionalData: {
				searchType: 'port-geometry',
				portName: action.payload.portGeometry.properties.portName,
				country: action.payload.portGeometry.properties.country,
				status: 'success',
				vesselsFound: filteredSubscribedVessels.length,
			},
		});
	} catch (error) {

		// ✅ Improved error tracking
		trackExceptionWithContext(error as Error, {
			component: AppInsightsComponents.SEARCH_SLICE,
			action: AppInsightsActions.SEARCH_QUERY_SUBMIT,
			additionalData: {
				errorType: AppInsightsErrorTypes.SEARCH_ENTITY_NOT_FOUND_ERROR,
				searchType: 'port-geometry',
				portName: action.payload.portGeometry?.properties?.portName || 'Unknown',
				hasGeometry: !!action.payload.portGeometry?.geometry,
			},
		});
	}
}

export function* watchPortActions(): Generator {
	yield takeLatest(portSagaActionTypes.SEARCH_PORTS_REQUEST, searchPortsSaga);
	yield takeLatest(portSagaActionTypes.GET_PORT_GEOMETRY_BY_NAME_AND_COUNTRY_REQUEST, getPortGeometryByNameAndCountrySaga);
	yield takeLatest(portSagaActionTypes.DISPLAY_OUTPUT_BY_PORT_GEOMETRY_REQUEST, displayOutputByPortGeometrySaga);
}
